import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/apiClient";
import { AdminHeader, AdminPage } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash2, Pencil, ArrowUp, ArrowDown, Upload } from "lucide-react";

const EMPTY = { name: "", designation: "", bio: "", background: "", responsibilities: "", message: "", is_chairman: false, published: true, order: 99, photo_id: null, photo_path: null };
function Field({ label, children }) { return <div className="space-y-1.5"><label className="label-eyebrow text-slate-500 block">{label}</label>{children}</div>; }

export default function LeadershipAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const photoRef = useRef();
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const load = () => api.get("/leadership?include_unpublished=true").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("is_public", "true");
    try { const { data } = await api.post("/media/upload", fd); setF((p) => ({ ...p, photo_id: data.id, photo_path: data.storage_path })); toast.success("Photo uploaded"); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setUploading(false); }
  };

  const edit = (l) => { setF({ ...EMPTY, ...l }); setOpen(true); };
  const save = async () => {
    if (!f.name) return toast.error("Name required");
    try { if (f.id) await api.put(`/leadership/${f.id}`, f); else await api.post("/leadership", f); toast.success("Saved"); setOpen(false); setF(EMPTY); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (l) => { if (!window.confirm("Delete member?")) return; await api.delete(`/leadership/${l.id}`); toast.success("Deleted"); load(); };
  const move = async (idx, dir) => {
    const arr = [...items]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    await api.post("/leadership/reorder", { order: arr.map((x) => x.id) });
  };

  return (
    <>
      <AdminHeader title="Leadership" subtitle={`${items.length} members`} actions={<Button onClick={() => { setF(EMPTY); setOpen(true); }} className="bg-npst-navy text-white" data-testid="add-leader-btn">Add Member</Button>} />
      <AdminPage>
        <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
          {items.length === 0 ? <p className="p-6 text-slate-400 text-sm">No leadership members yet.</p> : items.map((l, i) => (
            <div key={l.id} className="flex items-center justify-between px-4 py-3">
              <div><span className="font-medium text-npst-ink">{l.name}</span> <span className="text-slate-400 text-sm">· {l.designation}</span>{l.is_chairman && <span className="ml-2 text-[9px] bg-npst-goldLight text-npst-gold border border-npst-goldBorder px-1.5 py-0.5 rounded uppercase">Chairman</span>}</div>
              <div className="flex items-center gap-3 text-slate-400">
                <button onClick={() => move(i, -1)} className="hover:text-npst-navy"><ArrowUp size={15} /></button>
                <button onClick={() => move(i, 1)} className="hover:text-npst-navy"><ArrowDown size={15} /></button>
                <button onClick={() => edit(l)} className="hover:text-npst-navy"><Pencil size={15} /></button>
                <button onClick={() => del(l)} className="hover:text-red-600"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </AdminPage>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle className="font-serif text-2xl">{f.id ? "Edit" : "Add"} Member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              {f.photo_path ? <img src={fileUrl(f.photo_path)} alt="portrait" className="w-20 h-20 rounded-sm object-cover border border-slate-200" />
                : <div className="w-20 h-20 rounded-sm bg-slate-100 flex items-center justify-center text-slate-300 text-2xl font-serif">{f.name?.[0] || "N"}</div>}
              <div>
                <Button type="button" variant="outline" size="sm" onClick={() => photoRef.current?.click()} disabled={uploading} data-testid="leader-photo-btn"><Upload size={14} className="mr-1.5" /> {uploading ? "Uploading…" : "Upload Photo"}</Button>
                {f.photo_path && <button type="button" onClick={() => set("photo_path", null)} className="ml-3 text-xs text-red-600 hover:underline">Remove</button>}
                <input ref={photoRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => uploadPhoto(e.target.files[0])} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name"><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="leader-name" /></Field>
              <Field label="Designation"><Input value={f.designation} onChange={(e) => set("designation", e.target.value)} data-testid="leader-designation" /></Field>
            </div>
            <Field label="Biography"><Textarea rows={3} value={f.bio} onChange={(e) => set("bio", e.target.value)} /></Field>
            <Field label="Professional Background"><Textarea rows={2} value={f.background} onChange={(e) => set("background", e.target.value)} /></Field>
            <Field label="Message (Chairman)"><Textarea rows={2} value={f.message} onChange={(e) => set("message", e.target.value)} /></Field>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_chairman} onChange={(e) => set("is_chairman", e.target.checked)} /> Is Chairman</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.published} onChange={(e) => set("published", e.target.checked)} /> Published</label>
            </div>
            <Button onClick={save} className="w-full bg-npst-navy text-white" data-testid="leader-save-btn">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
